import React, { useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useWatch } from 'react-hook-form';
import { formatFloat } from '../../../../helpers';
import { DConfirmationModal, WarningIcon } from '../../../dum';

interface IFinalAmountChangeConfirmation {
    initialAmount: number;
    isEditing: boolean;
    subject: string;
}

interface IPendingConfirmation {
    amount: number;
    resolve: (confirmed: boolean) => void;
}

function normalizeAmount(amount: number | null | undefined) {
    const numericAmount = Number(amount);
    return Number.isNaN(numericAmount) ? 0 : numericAmount;
}

function hasAmountChanged(initialAmount: number, currentAmount: number) {
    const difference =
        Math.round((currentAmount - normalizeAmount(initialAmount)) * 100) /
        100;
    return Math.abs(difference) >= 0.01;
}

interface IFinalAmountChangeWarning {
    amountFieldName: string;
    initialAmount: number;
    isEditing: boolean;
    subject: string;
    enabledFieldName?: string;
    enabledValues?: number[];
}

export function FinalAmountChangeWarning({
    amountFieldName,
    initialAmount,
    isEditing,
    subject,
    enabledFieldName,
    enabledValues = [],
}: IFinalAmountChangeWarning) {
    const amount = useWatch({ name: amountFieldName });
    const enabledValue = useWatch({
        name: enabledFieldName ?? amountFieldName,
    });

    const currentAmount = normalizeAmount(amount);
    const enabled = enabledFieldName
        ? enabledValues.includes(Number(enabledValue))
        : true;

    if (
        !enabled ||
        !isEditing ||
        !hasAmountChanged(initialAmount, currentAmount)
    ) {
        return null;
    }

    return (
        <Box
            sx={{
                my: 1,
                px: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
            }}
        >
            <WarningIcon fontSize={'small'} color={'warning'} />
            <Typography
                variant={'body2'}
                color={'warning.main'}
                sx={{ fontWeight: 'bold' }}
            >
                {`${subject} cambió: $${formatFloat(
                    initialAmount,
                )} → $${formatFloat(currentAmount)}`}
            </Typography>
        </Box>
    );
}

export function useFinalAmountChangeConfirmation({
    initialAmount,
    isEditing,
    subject,
}: IFinalAmountChangeConfirmation) {
    const [pending, setPending] = useState<IPendingConfirmation | null>(null);

    const confirmFinalAmountChange = useCallback(
        (amount: number | null | undefined, enabled = true) => {
            const normalizedAmount = normalizeAmount(amount);

            if (
                !enabled ||
                !isEditing ||
                !hasAmountChanged(initialAmount, normalizedAmount)
            ) {
                return Promise.resolve(true);
            }

            return new Promise<boolean>((resolve) => {
                setPending({ amount: normalizedAmount, resolve });
            });
        },
        [initialAmount, isEditing],
    );

    const resolvePending = useCallback(
        (confirmed: boolean) => {
            pending?.resolve(confirmed);
            setPending(null);
        },
        [pending],
    );

    const finalAmountChangeConfirmation = (
        <DConfirmationModal
            open={!!pending}
            setOpen={(open) => {
                if (!open) resolvePending(false);
            }}
            callback={() => resolvePending(true)}
            title={'Confirmar cambio de total'}
            message={`${subject} cambió: $${formatFloat(
                initialAmount,
            )} → $${formatFloat(
                pending?.amount ?? initialAmount,
            )}. ¿Deseas guardar el cambio?`}
            confirmLabel={'Guardar cambio'}
            cancelLabel={'Cancelar'}
            confirmColor={'primary'}
            // This opens ON TOP of the sale/expense/transfer form, which is an
            // 'xl' modal. Inheriting that width made the two the same size, so
            // the confirmation read as the form redrawing itself rather than as
            // a separate question. Deliberately the smallest size, and sized to
            // its own content so a long amount can't be clipped by the fixed
            // height the delete confirmations use.
            size={'small'}
            tight
            height={'auto'}
            // The whole point of this dialog is that a saved total is about to
            // change, so the header carries the same warning colour as the
            // inline FinalAmountChangeWarning above.
            toolbarColor={'warning'}
        />
    );

    return {
        confirmFinalAmountChange,
        finalAmountChangeConfirmation,
    };
}
